import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Typography,
  Modal,
  Menu,
  Popover,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { isClosedByMinCount } from './classClosed';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonDatePicker from '../../../form-components/CommonDatePicker';
import {
  ConfirmModalContainer,
  SuccessModalContainer,
  BusyDatesModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  ModalContent,
  ConfirmModalContent,
  ConfirmModalTitle,
  ConfirmModalText,
  ConfirmModalButtonGroup,
  MenuContainer,
  MenuHeader,
  MenuTitle,
  MenuSubtitle,
  MenuBody,
  MenuRow,
  MenuLabel,
  MenuValue,
  MenuButtonGroup,
  MenuButton,
  TooltipLessonHeader,
  TooltipLessonTitle,
  TooltipStatusBadge,
  TooltipBody,
  TooltipInfoRow,
  TooltipInfoLabel,
  TooltipInfoValue,
  DeleteButton,
  CopyWeeklyModalContainer,
  CopyWeeklyHeader,
  CopyWeeklyHeaderTitle,
  CopyWeeklyContent,
  CopyWeeklyDescription,
  CopyWeeklyFormRow,
  CopyWeeklyLabel,
  CopyWeeklyButtonGroup,
  CancelTypeStyledDialog,
  CancelTypeRow,
  CancelTypeLabel,
} from './GroupLessonStyled';

// 수업 취소 확인 모달
export const CancelConfirmModal = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose}>
      <ConfirmModalContainer>
        <ModalHeader>
          <ModalHeaderTitle>{t('수업 취소 확인')}</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer' }} />
        </ModalHeader>
        <ConfirmModalContent>
          <ConfirmModalTitle>{t('수업을 취소하시겠습니까?')}</ConfirmModalTitle>
          <Box>
            <ConfirmModalText>{t('*수업 취소 전 반드시 회원 및 강사에게')}</ConfirmModalText>
            <ConfirmModalText>{t('취소 사유를 알려주시기 바랍니다')}.</ConfirmModalText>
          </Box>
        </ConfirmModalContent>
        <ConfirmModalButtonGroup>
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('돌아가기')}
          </CommonModalButton>
          <CommonModalButton variant="delete" onClick={onConfirm}>
            {t('수업 취소하기')}
          </CommonModalButton>
        </ConfirmModalButtonGroup>
      </ConfirmModalContainer>
    </Modal>
  );
};

// 수업 취소 완료 모달
export const CancelSuccessModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal open={open}>
      <SuccessModalContainer>
        <Box>
          <Typography sx={{ fontSize: '18px', mt: '80px', ml: '117px' }}>{t('예약 취소를 완료하였습니다')}</Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              borderRadius: '10px',
              mt: '52px',
              ml: '189px',
              pl: '20px',
              pr: '15px',
            }}
            onClick={onClose}>
            {t('확인')}
          </Button>
        </Box>
      </SuccessModalContainer>
    </Modal>
  );
};

// 중복 날짜 모달
export const BusyDatesModal = ({ open, busyDates, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <BusyDatesModalContainer>
        <ModalHeader>
          <ModalHeaderTitle>{t('이미 있습니다')}</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer' }} />
        </ModalHeader>
        <ModalContent>
          {busyDates?.map((data, index) => (
            <Typography sx={{ fontSize: '18px' }} key={index}>
              <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{data.day}</span> ({data.timeStart} -{' '}
              {data.timeEnd})
            </Typography>
          ))}
        </ModalContent>
      </BusyDatesModalContainer>
    </Modal>
  );
};

// 수업 취소 타입 선택 다이얼로그
export const CancelTypeDialog = ({ open, onClose, onConfirm, isLoading }) => {
  return (
    <CancelTypeStyledDialog open={open} onClose={onClose} aria-labelledby="cancel-dialog-title" maxWidth="xs" fullWidth>
      <ModalHeader>
        <ModalHeaderTitle>수업 취소 확인</ModalHeaderTitle>
        <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer' }} />
      </ModalHeader>
      <DialogContent sx={{ padding: '28px 24px' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>
          해당 수업을 취소하시겠습니까?
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#888', mt: '10px' }}>
          예약된 회원이 있는 경우 취소가 불가능합니다.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: '8px' }}>
        <CommonModalButton variant="cancel" onClick={onClose} disabled={isLoading}>
          닫기
        </CommonModalButton>
        <CommonModalButton variant="delete" onClick={onConfirm} disabled={isLoading}>
          {isLoading ? '취소 중...' : '수업 취소'}
        </CommonModalButton>
      </DialogActions>
    </CancelTypeStyledDialog>
  );
};

// 주간 복사 확인 모달
export const CopyWeeklyConfirmModal = ({ open, onClose, onConfirm, openYn, onOpenYnChange, isLoading, sourceFrom, sourceTo, targetFrom, targetTo, onSourceFromChange, onSourceToChange, onTargetFromChange, onTargetToChange }) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <CopyWeeklyModalContainer>
        <CopyWeeklyHeader>
          <CopyWeeklyHeaderTitle>{t('일정 복사')}</CopyWeeklyHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </CopyWeeklyHeader>
        <CopyWeeklyContent>
          <CopyWeeklyDescription>원본 기간의 수업을 대상 기간으로 복사합니다.</CopyWeeklyDescription>

          {/* 원본 기간 (현재 캘린더 주로 고정) */}
          <CopyWeeklyLabel sx={{ mb: '8px' }}>원본 기간 (현재 주)</CopyWeeklyLabel>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', mb: '16px' }}>
            <CommonDatePicker
              value={sourceFrom}
              onChange={() => {}}
              disabled
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
                '& .MuiIconButton-root': { display: 'none' },
              }}
            />
            <Typography sx={{ color: '#999' }}>~</Typography>
            <CommonDatePicker
              value={sourceTo}
              onChange={() => {}}
              disabled
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
                '& .MuiIconButton-root': { display: 'none' },
              }}
            />
          </Box>

          {/* 대상 기간 */}
          <CopyWeeklyLabel sx={{ mb: '8px' }}>대상 기간</CopyWeeklyLabel>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', mb: '16px' }}>
            <CommonDatePicker
              value={targetFrom}
              onChange={date => onTargetFromChange(date ? moment(date).format('YYYY-MM-DD') : '')}
              placeholder="시작일"
              sx={{ flex: 1 }}
            />
            <Typography sx={{ color: '#999' }}>~</Typography>
            <CommonDatePicker
              value={targetTo}
              onChange={date => onTargetToChange(date ? moment(date).format('YYYY-MM-DD') : '')}
              minDate={targetFrom}
              placeholder="종료일"
              sx={{ flex: 1 }}
            />
          </Box>

          {/* 복사 안내 및 기간 불일치 경고 */}
          {sourceFrom && sourceTo && targetFrom && targetTo && (() => {
            const dayLabels = ['일', '월', '화', '수', '목', '금', '토'];
            const targetStart = new Date(targetFrom);
            const targetEnd = new Date(targetTo);
            const sourceStart = new Date(sourceFrom);
            const sourceEnd = new Date(sourceTo);
            const targetDays = Math.round((targetEnd - targetStart) / 86400000) + 1;
            const weeks = Math.floor(targetDays / 7);
            const extraDays = targetDays % 7;
            const totalWeeks = weeks + (extraDays > 0 ? 1 : 0);

            // 마지막 주에서 빠지는 요일/날짜
            const missingList = [];
            if (extraDays > 0) {
              const lastWeekStart = new Date(targetStart);
              lastWeekStart.setDate(lastWeekStart.getDate() + weeks * 7);
              const lastWeekDaySet = new Set();
              for (let d = new Date(lastWeekStart); d <= targetEnd; d.setDate(d.getDate() + 1)) {
                lastWeekDaySet.add(d.getUTCDay());
              }
              for (let d = new Date(sourceStart); d <= sourceEnd; d.setDate(d.getDate() + 1)) {
                if (!lastWeekDaySet.has(d.getUTCDay())) {
                  const missDate = new Date(lastWeekStart);
                  const offset = (d.getUTCDay() - lastWeekStart.getUTCDay() + 7) % 7;
                  missDate.setDate(missDate.getDate() + offset);
                  missingList.push({ day: dayLabels[d.getUTCDay()], date: `${missDate.getUTCMonth() + 1}/${missDate.getUTCDate()}` });
                }
              }
            }
            const uniqueMissing = missingList.filter((v, i, a) => a.findIndex(t => t.day === v.day) === i);

            return (
              <>
                {/* 기본 안내 */}
                <Box sx={{ mb: '12px', p: '10px 14px', borderRadius: '6px', backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7' }}>
                  <Typography sx={{ fontSize: '13px', color: '#2E7D32', fontWeight: 600 }}>
                    원본 주의 수업이 대상 기간에 {totalWeeks}주 생성됩니다.
                  </Typography>
                </Box>

                {/* 빠지는 날짜 경고 */}
                {uniqueMissing.length > 0 && (
                  <Box sx={{ mb: '12px', p: '10px 14px', borderRadius: '6px', backgroundColor: '#FFF8E1', border: '1px solid #FFE082' }}>
                    <Typography sx={{ fontSize: '13px', color: '#F57F17', fontWeight: 600 }}>
                      ⚠ 마지막 주 일부 수업이 생성되지 않습니다.
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#A67C00', mt: '4px' }}>
                      생성 안 됨: {uniqueMissing.map(m => `${m.date}(${m.day})`).join(', ')}
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}

          {/* 공개 설정 */}
          <CopyWeeklyLabel sx={{ mb: '8px' }}>{t('공개 설정')}</CopyWeeklyLabel>
          <RadioGroup value={openYn} onChange={e => onOpenYnChange(e.target.value)}>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Box
                onClick={() => !isLoading && onOpenYnChange('Y')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 14px', borderRadius: '8px',
                  border: openYn === 'Y' ? '2px solid #4AB3BC' : '1px solid #ddd',
                  backgroundColor: openYn === 'Y' ? '#f0fafa' : '#fff',
                  cursor: isLoading ? 'default' : 'pointer', transition: 'all 0.2s ease',
                }}>
                <Radio value="Y" checked={openYn === 'Y'} disabled={isLoading}
                  sx={{ color: '#4AB3BC', '&.Mui-checked': { color: '#4AB3BC' }, p: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>공개</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#888' }}>바로 예약 가능</Typography>
                </Box>
              </Box>
              <Box
                onClick={() => !isLoading && onOpenYnChange('N')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 14px', borderRadius: '8px',
                  border: openYn === 'N' ? '2px solid #4AB3BC' : '1px solid #ddd',
                  backgroundColor: openYn === 'N' ? '#f0fafa' : '#fff',
                  cursor: isLoading ? 'default' : 'pointer', transition: 'all 0.2s ease',
                }}>
                <Radio value="N" checked={openYn === 'N'} disabled={isLoading}
                  sx={{ color: '#4AB3BC', '&.Mui-checked': { color: '#4AB3BC' }, p: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>비공개</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#888' }}>나중에 공개 가능</Typography>
                </Box>
              </Box>
            </Box>
          </RadioGroup>
        </CopyWeeklyContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', p: '16px 24px', borderTop: '1px solid #eee' }}>
          <CommonModalButton variant="cancel" onClick={onClose} disabled={isLoading}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? t('복사 중...') : t('복사하기')}
          </CommonModalButton>
        </Box>
      </CopyWeeklyModalContainer>
    </Modal>
  );
};

// 이벤트 메뉴
export const EventMenu = ({ anchorEl, open, onClose, eventData, onAttendance, onEdit, onCancel, canEdit = true, canAttendance = true, canCancel = true, hideWaiting = false, hideCancel = false }) => {
  // 상태 계산
  const getStatus = () => {
    if (!eventData) return 'none';
    const { openYn, closeDate, reservedMemberCount = 0, maxMemberCount = 0, attendedMemberCount = 0, absentedMemberCount = 0, classDate, endTime } = eventData;
    const isPast = moment().isAfter(moment(`${classDate} ${endTime}`, 'YYYY-MM-DD HH:mm'));
    if (isClosedByMinCount(eventData)) return 'canceledByCount';
    if (isPast) return 'past';
    if (openYn === 'N') return 'closed';
    if (reservedMemberCount + attendedMemberCount >= maxMemberCount) return 'full';
    if (reservedMemberCount === 0 && attendedMemberCount === 0 && absentedMemberCount === 0) return 'none';
    return 'under';
  };
  const STATUS = {
    past:   { badge: '#BFBFBF', font: '#404040', label: '종료' },
    closed: { badge: '#D1D1D1', font: '#404040', label: '비공개' },
    canceledByCount: { badge: '#5C5C5C', font: '#FFFFFF', label: '폐강' },
    full:   { badge: '#A7E3BE', font: '#247E46', label: '예약마감' },
    none:   { badge: '#F6B6A9', font: '#9E230B', label: '예약없음' },
    under:  { badge: '#FFD9A9', font: '#AA6000', label: '예약미달' },
  };
  const status = getStatus();
  const statusConfig = STATUS[status] || STATUS.none;
  const currentCount = (eventData?.reservedMemberCount || 0) + (eventData?.attendedMemberCount || 0);

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      sx={{
        ml: '8px',
        '& .MuiPaper-root': {
          bgcolor: '#fff', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          border: '1px solid #E0E3E6', minWidth: '210px',
        },
      }}>
      <Box sx={{ p: '10px' }}>
        <TooltipLessonHeader sx={{ m: '-10px -10px 0', borderRadius: '8px 8px 0 0' }}>
          <TooltipLessonTitle>{eventData?.className}</TooltipLessonTitle>
          <TooltipStatusBadge badgeBg={statusConfig.badge} badgeFont={statusConfig.font}>
            {statusConfig.label}
          </TooltipStatusBadge>
        </TooltipLessonHeader>
        <TooltipBody sx={{ m: '0 -10px' }}>
          <TooltipInfoRow>
            <TooltipInfoLabel>시간</TooltipInfoLabel>
            <TooltipInfoValue>{eventData?.startTime} - {eventData?.endTime}</TooltipInfoValue>
          </TooltipInfoRow>
          <TooltipInfoRow>
            <TooltipInfoLabel>강사</TooltipInfoLabel>
            <TooltipInfoValue>{eventData?.trainerName}</TooltipInfoValue>
          </TooltipInfoRow>
          {eventData?.branchExerciseRoomName && (
            <TooltipInfoRow>
              <TooltipInfoLabel>강의실</TooltipInfoLabel>
              <TooltipInfoValue>{eventData?.branchExerciseRoomName}</TooltipInfoValue>
            </TooltipInfoRow>
          )}
          <TooltipInfoRow>
            <TooltipInfoLabel>예약</TooltipInfoLabel>
            <TooltipInfoValue valueColor="#0F7F85">{currentCount} / {eventData?.maxMemberCount || 0}명</TooltipInfoValue>
          </TooltipInfoRow>
          {/* OT 처럼 대기열이 없는 수업은 숨긴다 */}
          {!hideWaiting && (
            <TooltipInfoRow>
              <TooltipInfoLabel>대기</TooltipInfoLabel>
              <TooltipInfoValue valueColor="#E67E22">{eventData?.waitingMemberCount || 0}명 / {eventData?.membersWaitMaxCount || 0}명</TooltipInfoValue>
            </TooltipInfoRow>
          )}
          <TooltipInfoRow>
            <TooltipInfoLabel>출석</TooltipInfoLabel>
            <TooltipInfoValue>{eventData?.attendedMemberCount || 0}명</TooltipInfoValue>
          </TooltipInfoRow>
          <TooltipInfoRow>
            <TooltipInfoLabel>결석</TooltipInfoLabel>
            <TooltipInfoValue>{eventData?.absentedMemberCount || 0}명</TooltipInfoValue>
          </TooltipInfoRow>
          {eventData?.openDate && (
            <TooltipInfoRow>
              <TooltipInfoLabel>자동공개</TooltipInfoLabel>
              <TooltipInfoValue>{moment(eventData.openDate).format('M/D HH:mm')}</TooltipInfoValue>
            </TooltipInfoRow>
          )}
          {eventData?.closeDate && (
            <TooltipInfoRow>
              <TooltipInfoLabel>자동폐강</TooltipInfoLabel>
              <TooltipInfoValue>{moment(eventData.closeDate).format('M/D HH:mm')} / {eventData.closeMemberCount ?? 0}명 이하</TooltipInfoValue>
            </TooltipInfoRow>
          )}
        </TooltipBody>
        <Box sx={{ m: '0 -10px -10px', borderTop: '1px solid #EDF0F2', display: 'flex', gap: '1px', backgroundColor: '#EDF0F2', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
          {[
            { label: '참석관리', onClick: onAttendance, can: canAttendance, color: '#0F7F85', bg: '#F0F9F9' },
            { label: '수업수정', onClick: onEdit, can: canEdit, color: '#555', bg: '#F8F8F8' },
            { label: '수업취소', onClick: onCancel, can: canCancel, color: '#D32F2F', bg: '#FEF5F5', hidden: hideCancel },
          ].filter(btn => !btn.hidden).map((btn) => (
            <Box key={btn.label}
              onClick={btn.can ? btn.onClick : undefined}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '9px 0', cursor: btn.can ? 'pointer' : 'default',
                fontSize: '12px', fontWeight: 700, color: btn.can ? btn.color : '#ccc',
                backgroundColor: btn.can ? '#fff' : '#fafafa',
                '&:hover': btn.can ? { backgroundColor: btn.bg } : {},
                '&:active': btn.can ? { backgroundColor: btn.bg, transform: 'scale(0.97)' } : {},
                transition: 'all 0.1s',
              }}>
              {btn.label}
            </Box>
          ))}
        </Box>
      </Box>
    </Popover>
  );
};
